/**
 * Format validation for Technical Log Book entry fields.
 * Used to highlight invalid values in the entry form (red TextField border).
 */

export const TLB_FORMAT = {
  HHMM: 'HHMM',
  HHmm: 'HHmm',
  HMM: 'HMM',
  HHmmColon: 'HH:mm',
  HOURS: 'HOURS'
};

const PATH_FORMATS = {
  rotorStartTimeUTC: { format: TLB_FORMAT.HHMM, clock: true },
  rotorStopTimeUTC: { format: TLB_FORMAT.HHMM, clock: true },
  blockTime: { format: TLB_FORMAT.HHMM, duration: true },
  airboneTime: { format: TLB_FORMAT.HMM },
  'defectReport.pilotDefectTime': { format: TLB_FORMAT.HHmmColon },
  'defectReport.ameDefectTime': { format: TLB_FORMAT.HHmm },
  'bffCertification.bffTime': { format: TLB_FORMAT.HHmm },
  'pilotAcceptance.acceptanceTime': { format: TLB_FORMAT.HHmm }
};

const HHMM_SUFFIX_PATHS = ['.inspectionTime', '.cleaningTime'];
const HHmm_SUFFIX_PATHS = ['.ameDefectTime', '.bffTime', '.acceptanceTime'];
const HHmmColon_SUFFIX_PATHS = ['.pilotDefectTime'];

export function getFormatSpecForPath(path) {
  if (!path || typeof path !== 'string') return null;
  if (PATH_FORMATS[path]) return PATH_FORMATS[path];
  if (HHMM_SUFFIX_PATHS.some((suffix) => path.endsWith(suffix))) {
    return { format: TLB_FORMAT.HHmm };
  }
  if (HHmm_SUFFIX_PATHS.some((suffix) => path.endsWith(suffix))) {
    return { format: TLB_FORMAT.HHmm };
  }
  if (HHmmColon_SUFFIX_PATHS.some((suffix) => path.endsWith(suffix))) {
    return { format: TLB_FORMAT.HHmmColon };
  }
  return null;
}

function isEmptyValue(value) {
  return value == null || String(value).trim() === '';
}

function isValidHHMM(value, { clock = false, duration = false } = {}) {
  const text = String(value).trim();
  if (!/^\d{4}$/.test(text)) return false;
  const hours = Number(text.slice(0, 2));
  const minutes = Number(text.slice(2, 4));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes >= 60) return false;
  if (clock && hours >= 24) return false;
  if (!clock && !duration && hours >= 24) return false;
  return true;
}

function isValidHHmm(value) {
  const text = String(value).trim();
  if (!/^\d{3,4}$/.test(text)) return false;
  const padded = text.padStart(4, '0');
  const minutes = Number(padded.slice(2, 4));
  return Number.isFinite(minutes) && minutes < 60;
}

function isValidHMM(value) {
  const text = String(value).trim();
  const match = text.match(/^(\d+):(\d{1,2})$/);
  if (!match) return false;
  const minutes = Number(match[2]);
  return Number.isFinite(minutes) && minutes >= 0 && minutes < 60;
}

function isValidHHmmColon(value) {
  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return false;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
}

function isValidHours(value) {
  const text = String(value).trim();
  if (!text.includes(':')) {
    const decimal = Number(text.replace(',', '.'));
    return Number.isFinite(decimal);
  }
  const match = text.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (!match) return false;
  const minutes = Number(match[2]);
  const seconds = match[3] != null ? Number(match[3]) : 0;
  return minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60;
}

export function isValidTlbFormat(value, spec) {
  if (isEmptyValue(value)) return true;
  if (!spec) return true;

  switch (spec.format) {
    case TLB_FORMAT.HHMM:
      return isValidHHMM(value, spec);
    case TLB_FORMAT.HHmm:
      return isValidHHmm(value);
    case TLB_FORMAT.HMM:
      return isValidHMM(value);
    case TLB_FORMAT.HHmmColon:
      return isValidHHmmColon(value);
    case TLB_FORMAT.HOURS:
      return isValidHours(value);
    default:
      return true;
  }
}

export function getFormatHint(spec, path) {
  if (!spec) return '';
  switch (spec.format) {
    case TLB_FORMAT.HHMM:
      if (path === 'rotorStartTimeUTC') {
        return 'HHMM (UTC), e.g. 0930';
      }
      if (path === 'rotorStopTimeUTC') {
        return 'HHMM (UTC), e.g. 1545';
      }
      if (path === 'blockTime') {
        return 'HHMM duration, e.g. 0125';
      }
      return 'HHMM, e.g. 0930';
    case TLB_FORMAT.HHmm:
      return 'HHmm, e.g. 0930';
    case TLB_FORMAT.HMM:
      return 'H:MM, e.g. 2:30';
    case TLB_FORMAT.HHmmColon:
      return 'HH:mm, e.g. 09:30';
    case TLB_FORMAT.HOURS:
      return 'H:MM or decimal hours';
    default:
      return '';
  }
}

/**
 * Returns MUI TextField props ({ error, helperText }) for format-aware fields.
 * @param {unknown} value
 * @param {string|object} pathOrOptions - path string or { path, fieldType }
 * @param {string|undefined} validHelperText - helper text when the value is valid
 */
export function tlbFormatFieldProps(value, pathOrOptions, validHelperText) {
  const options = typeof pathOrOptions === 'string' ? { path: pathOrOptions } : (pathOrOptions || {});
  const spec = options.fieldType === 'hours'
    ? { format: TLB_FORMAT.HOURS }
    : getFormatSpecForPath(options.path);

  if (!spec) {
    return validHelperText ? { helperText: validHelperText } : {};
  }

  const valid = isValidTlbFormat(value, spec);
  const formatHint = getFormatHint(spec, options.path);

  if (!valid) {
    return {
      error: true,
      helperText: `Invalid format. Expected ${formatHint}`
    };
  }

  if (validHelperText) {
    return { helperText: validHelperText };
  }

  if (formatHint) {
    return { helperText: `Format: ${formatHint}` };
  }

  return {};
}

const OIL_QUANTITY_PATH_PATTERN =
  /(^|\.)((lh|rh)EngineOil(InTankBF|Uplifted|BeforeFlight|AfterFlight|Consumption)|oilConsumption)$/i;

const OIL_NON_QUANTITY_LABEL_PATTERN = /(temp|temperature|pressure|level)/i;

function isOilQuantityPath(path) {
  return OIL_QUANTITY_PATH_PATTERN.test(path || '');
}

function isOilQuantityLabel(label) {
  const text = String(label || '');
  return /oil/i.test(text) && !OIL_NON_QUANTITY_LABEL_PATTERN.test(text);
}

/**
 * Oil quantities are recorded in ml. Rewrite labels that mark oil in kg,
 * and add (ml) to known oil quantity fields that have no unit.
 */
export function formatTechlogOilQuantityLabel(label, path = '') {
  const raw = (label && String(label).trim()) ? String(label) : '';
  const knownOilQty = isOilQuantityPath(path);
  const marksOilInKg = isOilQuantityLabel(raw) && /\bkg\b/i.test(raw);

  if (!knownOilQty && !marksOilInKg) return raw || label;

  if (/\bkg\b/i.test(raw)) return raw.replace(/\bkg\b/gi, 'ml');
  if (/\bml\b/i.test(raw)) return raw;
  return raw ? `${raw} (ml)` : raw;
}
